import { type JSX, type ReactNode, useId, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { Abschlussreihe } from './Abschlussreihe';
import { Dialog } from './Dialog';
import { Meldung } from './Meldung';

/**
 * DIE RÜCKFRAGE IM POP-UP (Auftrag CRM-103, Regel in docs/arbeitsstand/oberflaechenregeln.md).
 *
 * Eine Aktion, die SOFORT wirkt und schwer umkehrbar ist — Storno, Löschen,
 * Festschreiben, Versand nach außen, Geld, Verbindlichmachen, Zugänge,
 * Überschreiben —, fragt hier nach, bevor sie etwas an die API schickt.
 *
 * Der Titel ist die Frage („Buchung stornieren?"), der Inhalt nennt die Folge
 * mit den Zahlen, die zählen. Der Eingriffsknopf trägt die Tat („Stornieren"),
 * nicht „OK". Der Fokus steht beim Öffnen auf „Abbrechen": Wer reflexhaft
 * Enter drückt, löst nichts aus.
 *
 * Während die Anfrage läuft, ist der Knopf gesperrt und das Pop-up lässt sich
 * nicht schließen; ein Fehler erscheint IM Pop-up, und es bleibt offen. Erst
 * der Erfolg schließt es — das erledigt der Aufrufer in `beiBestaetigen`,
 * meist indem er den Zustand zurücksetzt, der das Pop-up zeigt.
 *
 * Kein `window.confirm`: Das Eingabefenster des Browsers lässt sich nicht
 * gestalten, nennt keine Zahlen und kennt keinen Fehler.
 */
export function Rueckfrage({
  frage,
  unterzeile,
  tat,
  grund,
  gesperrt = false,
  beiBestaetigen,
  beiSchliessen,
  children,
}: {
  /** Die Frage als Titel, mit Fragezeichen: „Buchung stornieren?" */
  readonly frage: string;
  /** Worum es geht — Belegnummer, Name, Termin. */
  readonly unterzeile?: string | undefined;
  /** Die Beschriftung des Eingriffsknopfs, die Tat selbst: „Stornieren". */
  readonly tat: string;
  /**
   * Ein Pflichtgrund, der erst hier abgefragt wird. Steht der Grund schon im
   * Formular dahinter, zeigt ihn der Inhalt an — er wird nicht doppelt
   * verlangt.
   */
  readonly grund?: { readonly beschriftung: string; readonly hilfe?: string } | undefined;
  /** Weitere Sperre des Eingriffsknopfs, etwa solange eine Vorschau fehlt. */
  readonly gesperrt?: boolean;
  /** Löst die Aktion aus. Wirft sie, bleibt das Pop-up mit dem Fehler offen. */
  readonly beiBestaetigen: (grund: string) => Promise<unknown> | void;
  readonly beiSchliessen: () => void;
  /** Ein bis zwei Sätze zur Folge. */
  readonly children: ReactNode;
}): JSX.Element {
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | Error | null>(null);
  const [text, setzeText] = useState('');
  const grundId = useId();

  const ohneGrund = grund !== undefined && text.trim() === '';

  const bestaetigen = async (): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      await beiBestaetigen(text.trim());
    } catch (ursache) {
      setzeFehler(ursache instanceof Error ? ursache : new Error(String(ursache)));
    } finally {
      setzeLaeuft(false);
    }
  };

  // Solange die Anfrage läuft, führt auch Esc nicht hinaus: Das Ergebnis käme sonst ins Leere.
  const schliessen = (): void => {
    if (!laeuft) beiSchliessen();
  };

  return (
    <Dialog titel={frage} unterzeile={unterzeile} breite="schmal" beiSchliessen={schliessen}>
      <>
        {children}
        {grund !== undefined && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={grundId}>
              {grund.beschriftung} <span className="feld__pflicht">Pflicht</span>
            </label>
            <input
              id={grundId}
              className="feld__eingabe"
              value={text}
              onChange={(e) => setzeText(e.target.value)}
            />
            {grund.hilfe !== undefined && <p className="feld__hilfe">{grund.hilfe}</p>}
          </div>
        )}
        {fehler instanceof ApiFehler ? (
          <Meldung fehler={fehler} />
        ) : fehler !== null ? (
          <p className="meldung meldung--fehler" role="alert">
            {fehler.message}
          </p>
        ) : null}
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf" data-erstfokus disabled={laeuft} onClick={schliessen}>
              Abbrechen
            </button>
          }
        >
          <button
            type="button"
            className="knopf knopf--eingriff"
            disabled={laeuft || gesperrt || ohneGrund}
            aria-busy={laeuft}
            onClick={() => void bestaetigen()}
          >
            {tat}
          </button>
        </Abschlussreihe>
      </>
    </Dialog>
  );
}
